
import {
  defineComponent,
  computed,
  ComputedRef,
  Ref,
  ref,
  onMounted,
} from 'vue';

import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InlineMessage from 'primevue/inlinemessage';
import LoadElements from '@/components/template-builder/LoadElements.vue';
import { useI18n } from 'vue-i18n';
import {
  ElementPartTypeEnum,
  LoadSectionPayload,
  TemplateStructureEnum,
  TemplatePart,
} from '@/types';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

export default defineComponent({
  name: 'SearchElementsModal',

  components: {
    LoadElements,
    Dialog,
    Button,
    InlineMessage,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },

    elementType: {
      type: String,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },

    elementSubtype: {
      type: String,
      required: false,
      default: '',
    },

    includeAllElementsPartType: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['on-close-dialog', 'on-element-selected'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const displayModal: ComputedRef<boolean> = computed(() => props.displayDialog);
    const payload: Ref<LoadSectionPayload> = ref({ position: 'top', selector: '#spm_body .spm_section:first-child' });

    // Determine the types of elements to show based on the element type being saved
    const elementsTypes = computed(() => {
      if (props.elementType === TemplateStructureEnum.DESIGN) {
        return [TemplateStructureEnum.DESIGN];
      }
      if (props.elementType === TemplateStructureEnum.SECTION) {
        return [TemplateStructureEnum.SECTION];
      }
      if (props.elementType === TemplateStructureEnum.WIDGET) {
        return [TemplateStructureEnum.WIDGET];
      }
      // Default fallback
      return [props.elementType as TemplateStructureEnum];
    });

    const headerTitle = computed(() => {
      if (props.elementType === TemplateStructureEnum.DESIGN) {
        return 'templateBuilder.modals.searchElements.header.searchDesign';
      }
      if (props.elementPartType === ElementPartTypeEnum.STYLES) {
        return 'templateBuilder.modals.searchElements.header.searchStyle';
      }
      if (props.elementType === TemplateStructureEnum.SECTION) {
        return 'templateBuilder.modals.searchElements.header.searchSection';
      }
      if (props.elementType === TemplateStructureEnum.WIDGET) {
        return 'templateBuilder.modals.searchElements.header.searchWidget';
      }
      return 'templateBuilder.modals.searchElements.header.searchElement';
    });

    const loadActionTitle = computed(() => {
      if (props.elementType === TemplateStructureEnum.DESIGN) {
        return t('templateBuilder.modals.searchElements.actions.selectDesign');
      }
      if (props.elementPartType === ElementPartTypeEnum.STYLES) {
        return t('templateBuilder.modals.searchElements.actions.selectStyle');
      }
      if (props.elementType === TemplateStructureEnum.SECTION) {
        return t('templateBuilder.modals.searchElements.actions.selectSection');
      }
      if (props.elementType === TemplateStructureEnum.WIDGET) {
        return t('templateBuilder.modals.searchElements.actions.selectWidget');
      }
      return t('templateBuilder.modals.searchElements.actions.selectElement');
    });

    const handleClose = () => {
      emit('on-close-dialog');
    };

    const handleElementSelection = (item: TemplatePart) => {
      // Emit the selected element name instead of inserting it
      emit('on-element-selected', {
        elementName: item.name || '',
        elementId: item.id_template_parts,
      });
      handleClose();
    };

    const handleElementSelected = () => {
      // This is for backward compatibility with other events
      emit('on-element-selected');
      handleClose();
    };

    onMounted(() => {
      // We search for placeholder in the template
      const template = getTemplateIframeDocument();
      const placeholder = template?.querySelector('.sortable-group-section.sortable-placeholder') as HTMLElement;

      if (placeholder) {
        // We define the new payload
        payload.value = { position: 'bottom', selector: '.sortable-group-section.sortable-placeholder' };
      }
    });

    return {
      t,
      displayModal,
      payload,
      elementsTypes,
      headerTitle,
      loadActionTitle,
      handleClose,
      handleElementSelection,
      handleElementSelected,
    };
  },
});
